import React from 'react'
import * as PhosphorIcons from '@phosphor-icons/react'

const DeadlineAnalyzerViewResult = ({result}) => {
  const getPriorityColor = (priority) => {
    switch(priority?.toLowerCase()) {
      case 'high':
        return { bg: 'bg-error/20', text: 'text-error', icon: 'Warning' };
      case 'medium':
        return { bg: 'bg-warning/20', text: 'text-warning', icon: 'Clock' };
      case 'low':
        return { bg: 'bg-success/20', text: 'text-success', icon: 'CalendarCheck' };
      default:
        return { bg: 'bg-base-200', text: 'text-base-content', icon: 'Calendar' };
    }
  };

  const getDeadlineTypeColor = (type) => {
    switch(type?.toLowerCase()) {
      case 'hard':
        return 'bg-error/20 text-error';
      case 'soft':
        return 'bg-info/20 text-info';
      case 'recurring':
        return 'bg-secondary/20 text-secondary';
      default:
        return 'bg-base-200 text-base-content';
    }
  };

  const getAmbiguityColor = (level) => {
    switch(level) {
      case 'low':
        return 'bg-success/20 text-success';
      case 'medium':
        return 'bg-warning/20 text-warning';
      case 'high':
        return 'bg-error/20 text-error';
      default:
        return 'bg-base-200 text-base-content';
    }
  };

  return (
    <div className="mt-4 overflow-x-auto">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Deadlines Section */}
        <div className="space-y-4">
          {/* Deadlines Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-primary/10">
                  <PhosphorIcons.CalendarCheck size={20} weight="bold" className="text-primary"/>
                </div>
                <h3 className="card-title text-lg">Deadlines</h3>
              </div>

              <div className="space-y-4">
                {result?.deadline_analysis?.deadlines?.map((deadline, index) => (
                  <div key={index} className="p-4 bg-base-200 rounded-lg">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex flex-col">
                        <span className="font-medium text-lg">{deadline.date}</span>
                        <span className="text-base-content/70">{deadline.description}</span>
                      </div>
                      <div className="flex gap-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${getDeadlineTypeColor(deadline.type)}`}>
                          {deadline.type}
                        </span>
                        <span className="px-2 py-0.5 rounded-full text-xs bg-success/20 text-success">
                          {Math.round(deadline.confidence * 100)}%
                        </span>
                      </div>
                    </div>

                    {/* Context Section */}
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${getPriorityColor(deadline.context?.priority).bg} ${getPriorityColor(deadline.context?.priority).text}`}>
                          {deadline.context?.priority} priority
                        </span>
                      </div>

                      {/* Stakeholders */}
                      {deadline.context?.stakeholders?.length > 0 && (
                        <div className="space-y-1">
                          <span className="text-sm font-medium">Stakeholders:</span>
                          <div className="flex flex-wrap gap-2">
                            {deadline.context.stakeholders.map((stakeholder, idx) => (
                              <span key={idx} className="px-2 py-1 bg-base-300 rounded-full text-xs">
                                {stakeholder}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Dependencies */}
                      {deadline.context?.dependencies?.length > 0 && (
                        <div className="space-y-1">
                          <span className="text-sm font-medium">Dependencies:</span>
                          <div className="flex flex-wrap gap-2">
                            {deadline.context.dependencies.map((dependency, idx) => (
                              <span key={idx} className="px-2 py-1 bg-base-300 rounded-full text-xs">
                                {dependency}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Timeline and Metrics Section */}
        <div className="space-y-4">
          {/* Timeline Context Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-secondary/10">
                  <PhosphorIcons.Clock size={20} weight="bold" className="text-secondary"/>
                </div>
                <h3 className="card-title text-lg">Timeline Overview</h3>
              </div>

              <div className="space-y-3">
                {/* Timeline Range */}
                <div className="p-3 bg-base-200 rounded-lg space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Earliest Deadline</span>
                    <span className="px-2 py-0.5 rounded-full text-xs bg-primary/20 text-primary">
                      {result?.deadline_analysis?.timeline_context?.earliest_deadline}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Latest Deadline</span>
                    <span className="px-2 py-0.5 rounded-full text-xs bg-primary/20 text-primary">
                      {result?.deadline_analysis?.timeline_context?.latest_deadline}
                    </span>
                  </div>
                </div>

                {/* Critical Path */}
                {result?.deadline_analysis?.timeline_context?.critical_path?.length > 0 && (
                  <div className="p-3 bg-base-200 rounded-lg">
                    <h4 className="font-medium mb-2">Critical Path</h4>
                    <div className="space-y-2">
                      {result.deadline_analysis.timeline_context.critical_path.map((item, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <PhosphorIcons.ArrowRight size={16} className="text-primary"/>
                          <span className="text-sm">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Detection Metrics Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-info/10">
                  <PhosphorIcons.Gauge size={20} weight="bold" className="text-info"/>
                </div>
                <h3 className="card-title text-lg">Detection Metrics</h3>
              </div>

              <div className="space-y-3">
                {/* Confidence Score */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium">Confidence Score</span>
                    <span className="px-2 py-0.5 rounded-full text-xs bg-success/20 text-success">
                      {Math.round(result?.metadata?.detection_metrics?.confidence_score * 100)}%
                    </span>
                  </div>
                  <div className="w-full bg-base-300 rounded-full h-2">
                    <div 
                      className="h-2 rounded-full bg-success"
                      style={{width: `${result?.metadata?.detection_metrics?.confidence_score * 100}%`}}
                    ></div>
                  </div>
                </div>

                {/* Completeness */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium">Completeness</span>
                    <span className="px-2 py-0.5 rounded-full text-xs bg-primary/20 text-primary">
                      {Math.round(result?.metadata?.detection_metrics?.completeness * 100)}%
                    </span>
                  </div>
                  <div className="w-full bg-base-300 rounded-full h-2">
                    <div 
                      className="h-2 rounded-full bg-primary"
                      style={{width: `${result?.metadata?.detection_metrics?.completeness * 100}%`}}
                    ></div>
                  </div>
                </div>

                {/* Ambiguity Level */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Ambiguity Level</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${getAmbiguityColor(result?.metadata?.detection_metrics?.ambiguity_level)}`}>
                      {result?.metadata?.detection_metrics?.ambiguity_level}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeadlineAnalyzerViewResult